import * as postActions from '../actions/post.actions';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { EditorFormEntity } from '../models/form.models';
import { NomExportTransportTypes, NomObjectClass } from '../constants';
import { Store, select } from '@ngrx/store';
import {
  cancelShare,
  saveContextSettings,
  saveContextSettingsFail,
  saveConextSettingsSuccess as saveContextSettingsSuccess,
  share,
  shareCancelled,
  shareFailure,
  shareSuccess,
  updateObjectViewProperties,
  updateObjectViewPropertiesFail,
  updateObjectViewPropertiesSuccess,
} from '../actions/object.actions';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  selectCurrentObject,
  selectHomeContextSettings,
  selectUserWorkspaceContextSettings,
} from '../reducers';
import {
  setHomeSortingDirection,
  setHomeSortingField,
} from '../actions/home.actions';
import {
  setWorkspaceSortingDirection,
  setWorkspaceSortingField,
} from '../actions/workspace.actions';
import { submitFormFailure, submitFormSuccess } from '../actions/form.actions';
import {
  togglePeriodLocked,
  togglePeriodLockedFail,
  togglePeriodLockedSuccess,
} from '../actions/core.actions';

import { DashboardService } from '../services/dashboard.service';
import { Injectable } from '@angular/core';
import { ObjectService } from '../services/object.service';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';

// Replace with the actual path to your service

@Injectable()
export class ObjectEffects {
  togglePeriodLocked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(togglePeriodLocked),
      // grab the latest object from the store
      withLatestFrom(this.store.pipe(select(selectCurrentObject))),
      switchMap(([{ isPeriodLocked, period }, currentObject]) =>
        this.objectService
          .togglePeriodLocked(
            currentObject.id,
            currentObject.object_class_id,
            isPeriodLocked,
            period,
          )
          .pipe(
            map(() => togglePeriodLockedSuccess({ isPeriodLocked })),
            catchError((error) => of(togglePeriodLockedFail({ error }))),
          ),
      ),
    ),
  );

  updateObjectViewProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateObjectViewProperties),
      withLatestFrom(this.store.pipe(select(selectCurrentObject))),
      switchMap(([{ viewProperties }, currentObject]) => {
        if (
          currentObject &&
          currentObject.object_class_id === NomObjectClass.DASHBOARD
        ) {
          // Use the DashboardService for dashboards
          return this.dashboardService
            .saveDashboardSettings(currentObject.id, viewProperties)
            .pipe(
              map(() => updateObjectViewPropertiesSuccess({ viewProperties })),
              catchError((error) =>
                of(updateObjectViewPropertiesFail({ error })),
              ),
            );
        } else {
          return of(
            updateObjectViewPropertiesFail({
              error: 'Invalid object or unsupported object_class_id',
            }),
          );
        }
      }),
    ),
  );

  share$ = createEffect(() =>
    this.actions$.pipe(
      ofType(share),
      // 1) Start one export per share action
      mergeMap((action) =>
        this.objectService
          .share(
            action.objectId,
            action.objectClassId,
            action.postIds,
            action.contentFilters,
            action.settings,
            action.shareParams,
          )
          .pipe(
            // 2) Cancel the HTTP if matching cancelShare is dispatched
            takeUntil(
              this.actions$.pipe(
                ofType(cancelShare),
                filter((cancel) => cancel.objectId === action.objectId),
                tap(() => {
                  // 3) Dispatch cancellation manually (side-effect only)
                  this.store.dispatch(
                    shareCancelled({ objectId: action.objectId }),
                  );
                }),
              ),
            ),
            // 4) On success, emit both specific and generic success actions
            mergeMap((response) => [
              shareSuccess({ response }),
              submitFormSuccess({
                entity: EditorFormEntity.ExportForm,
                object: response,
                isCreated: false,
              }),
            ]),
            // 5) On failure, emit both specific and generic failure actions
            catchError((error) =>
              of(
                shareFailure({ error }),
                submitFormFailure({
                  entity: EditorFormEntity.ExportForm,
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  shareCancelled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(shareCancelled),
      // 1) do the toast as a side-effect
      tap(() =>
        this.toastService.showWarning(
          this.translate.instant('export-form.export-cancelled'),
        ),
      ),
      // 2) map into your failure action, grabbing an error payload if you have one.
      //    If your shareCancelled action carries an `error` property, pull it out:
      map(({ objectId }) =>
        submitFormFailure({
          entity: EditorFormEntity.ExportForm,
          error: {
            message: this.translate.instant('export-form.export-cancelled', {
              objectId,
            }),
          },
        }),
      ),
    ),
  );

  shareFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(shareFailure),
        tap(({ error }) => {
          this.toastService.showError(
            this.translate.instant('msg.export.export-failed', {
              error: error?.message || error,
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  shareFileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(shareSuccess),
        tap((action) => {
          if (action.response.type === NomExportTransportTypes.FILE) {
            if (action?.response?.download_url) {
              const link = document.createElement('a');
              link.href = action.response.download_url;
              link.download = 'download'; // Optional: Set the default filename for the download
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } else {
            }
          } else {
            this.toastService.showSuccess(
              this.translate.instant('msg.export.success'),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  contextSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setHomeSortingField, setHomeSortingDirection),
      // Combine the latest action with the current context settings from the store
      withLatestFrom(this.store.pipe(select(selectHomeContextSettings))),
      switchMap(([action, contextSettings]) =>
        this.objectService.saveContextSettings(contextSettings).pipe(
          // Dispatch success action if the save operation succeeds
          map(() => saveContextSettingsSuccess()),
          // Dispatch failure action if the save operation fails
          catchError((error) =>
            of(saveContextSettingsFail({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  workspaceContextSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setWorkspaceSortingField, setWorkspaceSortingDirection),
      // Combine the latest action with the current context settings from the store
      withLatestFrom(
        this.store.pipe(select(selectUserWorkspaceContextSettings)),
      ),
      switchMap(([action, contextSettings]) =>
        this.objectService.saveContextSettings(contextSettings).pipe(
          // Dispatch success action if the save operation succeeds
          map(() => saveContextSettingsSuccess()),
          // Dispatch failure action if the save operation fails
          catchError((error) =>
            of(saveContextSettingsFail({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private objectService: ObjectService,
    private store: Store,
    private translate: TranslateService,
    private dashboardService: DashboardService,
    private toastService: ToastService,
  ) {}
}
